/**
 * panorama.fixed.js - Video.js Panorama Plugin (Modernized for v8+)
 * Compatible with: Video.js 8.23.4
 * Author: Adapted by ChatGPT (2025)
 *
 * Usage:
 *   import 'video.js';
 *   import './panorama.fixed.js';
 *   const player = videojs('video');
 *   player.panorama({ videoType: 'equirectangular', ... });
 */

import videojs from 'video.js';
import {
  Mesh, Scene, Texture, WebGLRenderer, PerspectiveCamera, OrthographicCamera,
  PlaneGeometry, SphereBufferGeometry, MeshBasicMaterial, Vector3, Vector2, Math as ThreeMath
} from 'three';

(function() {
  const Component = videojs.getComponent('Component');
  const Button = videojs.getComponent('Button');

  /**
   * Simple helper to copy shallow properties.
   */
  const shallowCopy = (obj) => Object.assign({}, obj);

  /**
   * Notice component
   */
  class Notice extends Component {
    constructor(player, options = {}) {
      const el = document.createElement('div');
      el.className = 'vjs-video-notice-label';
      if (options.NoticeMessage) el.innerHTML = options.NoticeMessage;
      options.el = el;
      super(player, options);
    }
  }
  videojs.registerComponent('Notice', Notice);

  /**
   * Button notice component
   */
  class ButtonNotice extends Component {
    constructor(player, options = {}) {
      const el = document.createElement('div');
      el.className = 'vjs-video-ButtonNotice-label';
      if (options.ButtonNotice) el.innerHTML = options.ButtonNotice;
      options.el = el;
      super(player, options);
    }
  }
  videojs.registerComponent('buttonNotice', ButtonNotice);

  /**
   * Helper Canvas component
   */
  class HelperCanvas extends Component {
    constructor(player, options = {}) {
      const el = document.createElement('canvas');
      el.className = 'vjs-video-helper-canvas';
      el.width = options.width || 320;
      el.height = options.height || 180;
      el.style.display = 'none';
      options.el = el;
      super(player, options);
      this.videoElement = options.video;
      this.context = el.getContext('2d');
    }
    update() {
      if (this.context && this.videoElement)
        this.context.drawImage(this.videoElement, 0, 0, this.options_.width, this.options_.height);
    }
  }
  videojs.registerComponent('HelperCanvas', HelperCanvas);

  /**
   * VR Button
   */
  class VRButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText('VR');
    }
    handleClick() {
      const canvas = this.player().getChild('Canvas');
      if (!canvas) return;
      if (!canvas.VRMode) canvas.enableVR(); else canvas.disableVR();
      this.toggleClass('enable');
    }
  }
  videojs.registerComponent('VRButton', VRButton);

  /**
   * Upside Down Button
   */
  class UpsidedownButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText('Rotate');
    }
    handleClick() {
      const canvas = this.player().getChild('Canvas');
      if (!canvas) return;
      if (!canvas.USIDMode) canvas.enableUSIDMode(); else canvas.disableUSIDMode();
      this.toggleClass('enable');
    }
  }
  videojs.registerComponent('upsidedownButton', UpsidedownButton);

  /**
   * Base Panorama Canvas
   */
  class Canvas extends Component {
    constructor(player, options = {}) {
      super(player, options);
      this.settings = options;
      this.VRMode = !!options.isVR;
      this.USIDMode = !!options.isUpsidedown;
      this.width = player.el().offsetWidth;
      this.height = player.el().offsetHeight;

      this.scene = new Scene();
      this.camera = new PerspectiveCamera(options.initFov || 75, this.width / this.height, 1, 2000);
      this.camera.target = new Vector3(0, 0, 0);

      const video = player.tech({ IWillNotUseThisInPlugins: true }).el();
      const texture = new Texture(video);
      texture.minFilter = texture.magFilter = 1006;
      texture.format = 1022;

      this.renderer = new WebGLRenderer();
      this.renderer.setSize(this.width, this.height);

      const geometry = new SphereBufferGeometry(400, 60, 60);
      geometry.scale(-1, 1, 1);

      const material = new MeshBasicMaterial({ map: texture });
      this.mesh = new Mesh(geometry, material);
      this.scene.add(this.mesh);
      this.el().appendChild(this.renderer.domElement);

      player.on('play', () => this.animate());
    }
    animate() {
      this.requestId = requestAnimationFrame(() => this.animate());
      this.renderer.render(this.scene, this.camera);
    }
    enableVR() { this.VRMode = true; }
    disableVR() { this.VRMode = false; }
    enableUSIDMode() { this.USIDMode = true; }
    disableUSIDMode() { this.USIDMode = false; }
  }
  videojs.registerComponent('Canvas', Canvas);

  /**
   * Panorama plugin registration
   */
  const panoramaPlugin = function(options = {}) {
    this.ready(() => {
      this.addClass('vjs-panorama');
      this.addChild('Canvas', shallowCopy(options));
      if (options.VREnable) this.controlBar.addChild('VRButton', {}, this.controlBar.children().length - 1);
      if (options.upsidedownToggle) this.controlBar.addChild('upsidedownButton', {}, this.controlBar.children().length - 1);
    });
  };

  panoramaPlugin.VERSION = '8.23.4-fixed';
  videojs.registerPlugin('panorama', panoramaPlugin);
})();
